import { useTranslation } from 'react-i18next';
import { FieldData, Select, SubPartnerAccountStatus, TextField, useForm } from 'vopay';
import { usePartnerAccountStatusLabel } from '@/hooks';

/** Field names double as the inputs' `name`s (Cypress selects on `select-Status-trigger` / `textField-SearchText`). */
export type PartnerFilterValues = {
  /** Empty for no status filter. */
  Status: SubPartnerAccountStatus | '';
  SearchText: string;
};

type UsePartnerFilterFormConfigProps = {
  /** Comes from the URL, so the form re-seeds whenever the query string changes. */
  initialValues: PartnerFilterValues;
  /** The statuses the Status filter offers, in the order they should appear. */
  statuses: SubPartnerAccountStatus[];
  /** Disables the Status filter while its options are still being fetched. */
  isLoadingStatuses: boolean;
  onSubmit: (values: PartnerFilterValues) => void;
};

export default function usePartnerFilterFormConfig({
  initialValues,
  statuses,
  isLoadingStatuses,
  onSubmit,
}: UsePartnerFilterFormConfigProps) {
  const config = useConfig(statuses, isLoadingStatuses);

  const { ctx, renderFormElements } = useForm<PartnerFilterValues>({
    initialValues,
    config,
    onSubmit,
    enableReinitialize: true,
  });

  return { ctx, renderFormElements: () => renderFormElements({ config }) };
}

function useConfig(statuses: SubPartnerAccountStatus[], isLoadingStatuses: boolean): FieldData<PartnerFilterValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.SearchForm' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  // The raw status stays the option value (it is what the request filters on); only the label is
  // localized. No "All" option: a blank, clearable Select is the no-filter state.
  const statusOptions = statuses.map((status) => ({ label: resolveStatusLabel(status), value: status }));

  return [
    {
      fieldName: 'Status',
      getInputState: () => (isLoadingStatuses ? 'disabled' : 'optional'),
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          showClear
          {...getFormikData<PartnerFilterValues['Status']>({})}
          placeholder={t('statusPlaceholder', { defaultValue: 'Status' })}
          options={statusOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'SearchText',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <TextField
          {...getFormikData<PartnerFilterValues['SearchText']>({})}
          placeholder={t('keywordPlaceholder', { defaultValue: 'Keyword' })}
          variant="outlineShadow"
        />
      ),
    },
  ];
}
